import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Clock, DollarSign, TrendingUp } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useTranslation } from "react-i18next";

export function ROICalculator() {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();
  const [properties, setProperties] = useState(3);
  const [hoursPerMonth, setHoursPerMonth] = useState(10);

  // Calculation logic
  const hourlyRate = 25;
  const timeSavedPercentage = 60; // More realistic percentage
  
  // Base hours per property, scaled by number of properties
  const baseHoursPerProperty = 3; // Base assumption: 3 hours/property/month
  const totalBaseHours = baseHoursPerProperty * properties;
  const userHours = hoursPerMonth;
  
  // Time saved: percentage of user's actual time
  const calculatedTimeSaved = userHours * (timeSavedPercentage / 100);
  
  // Ensure minimum time saved (at least 1.5h per property)
  const minTimeSaved = properties * 1.5;
  const timeSaved = Math.max(calculatedTimeSaved, minTimeSaved);
  
  // Money calculations
  const grossSavings = timeSaved * hourlyRate;
  const appCost = properties <= 1 ? 0 : properties <= 5 ? 12 : 25;
  const netSavings = Math.max(grossSavings - appCost, 0);
  const yearlyNet = netSavings * 12;
  
  const handleGetStarted = () => {
    document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section ref={ref} className="py-20 bg-gradient-to-b from-secondary/30 to-background">
      <div className="container mx-auto px-4">
        <div
          className={`text-center mb-12 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t("landing.roi.title")}</h2>
          <p className="text-xl text-muted-foreground">{t("landing.roi.subtitle")}</p>
        </div>

        <Card
          className={`max-w-4xl mx-auto p-8 transition-all duration-700 delay-200 ${
            isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
          }`}
        >
          <div className="grid md:grid-cols-2 gap-8">
            {/* Inputs */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  {t("landing.roi.properties")}: <span className="text-primary font-bold">{properties}</span>
                </label>
                <Slider
                  value={[properties]}
                  onValueChange={(value) => setProperties(value[0])}
                  min={1}
                  max={10}
                  step={1}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  {t("landing.roi.hoursPerMonth")}: <span className="text-primary font-bold">{hoursPerMonth}h</span>
                </label>
                <Slider
                  value={[hoursPerMonth]}
                  onValueChange={(value) => setHoursPerMonth(value[0])}
                  min={5}
                  max={40}
                  step={5}
                  className="w-full"
                />
              </div>

              <div className="p-3 rounded-lg bg-muted/50 border border-border">
                <p className="text-xs text-muted-foreground">
                  <strong>{t("landing.roi.calculation")}:</strong><br/>
                  {t("landing.roi.timeSavedCalc", { percentage: timeSavedPercentage })}<br/>
                  {t("landing.roi.hourlyRate")}: €{hourlyRate}/h<br/>
                  {t("landing.roi.appCost")}: €{appCost}/month
                </p>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-primary/10 border border-primary/20 transition-all duration-300">
                <div className="flex items-center gap-2 text-primary mb-2">
                  <Clock className="w-5 h-5" />
                  <span className="font-semibold">{t("landing.roi.timeSaved")}</span>
                </div>
                <div className="text-3xl font-bold transition-all duration-300">{timeSaved.toFixed(1)}h/month</div>
                <div className="text-sm text-muted-foreground">{(timeSaved * 12).toFixed(0)}h/year</div>
              </div>

              <div className="p-4 rounded-lg bg-primary/10 border border-primary/20 transition-all duration-300">
                <div className="flex items-center gap-2 text-primary mb-2">
                  <DollarSign className="w-5 h-5" />
                  <span className="font-semibold">{t("landing.roi.moneySaved")}</span>
                </div>
                <div className="text-3xl font-bold transition-all duration-300">€{netSavings.toFixed(0)}/month</div>
                <div className="text-sm text-muted-foreground">€{yearlyNet.toFixed(0)}/year</div>
                <div className="text-xs text-muted-foreground mt-1">
                  ({t("landing.roi.gross")}: €{grossSavings.toFixed(0)} - {t("landing.roi.subscription")}: €{appCost})
                </div>
              </div>

              <div className="p-4 rounded-lg bg-primary/10 border border-primary/20 transition-all duration-300">
                <div className="flex items-center gap-2 text-primary mb-2">
                  <TrendingUp className="w-5 h-5" />
                  <span className="font-semibold">{t("landing.roi.roi")}</span>
                </div>
                <div className="text-3xl font-bold transition-all duration-300">
                  {appCost > 0 ? ((netSavings / appCost) * 100).toFixed(0) : '∞'}%
                </div>
                <div className="text-sm text-muted-foreground">{t("landing.roi.returnOnInvestment")}</div>
              </div>

              <Button 
                className="w-full hover-scale" 
                size="lg"
                onClick={handleGetStarted}
              >
                {t("landing.roi.cta")}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
}
